import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import AppShell from './components/layout/AppShell';
import AdminShell from './components/admin/AdminShell';

import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import DownloadPage from './pages/DownloadPage';
import LegalPage from './pages/LegalPage';
import DashboardPage from './pages/DashboardPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AskCoPilotPage from './pages/AskCoPilotPage';
import StartMeetingPage from './pages/StartMeetingPage';
import LiveMeetingPage from './pages/LiveMeetingPage';
import MeetingHistoryPage from './pages/MeetingHistoryPage';
import MeetingDetailsPage from './pages/MeetingDetailsPage';
import AutoAttendPage from './pages/AutoAttendPage';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import PlansPage from './pages/PlansPage';
import DocumentsPage from './pages/DocumentsPage';

import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUserDetailsPage from './pages/admin/AdminUserDetailsPage';
import AdminMeetingsPage from './pages/admin/AdminMeetingsPage';
import AdminMeetingDetailsPage from './pages/admin/AdminMeetingDetailsPage';
import AdminPricingPage from './pages/admin/AdminPricingPage';
import AdminBillingPage from './pages/admin/AdminBillingPage';
import AdminSystemPage from './pages/admin/AdminSystemPage';
import AdminAuditPage from './pages/admin/AdminAuditPage';
import AdminReleaseNotificationsPage from './pages/admin/AdminReleaseNotificationsPage';

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/*
        Public by necessity: the visitor has no session yet - that is
        precisely what this page exists to establish. Reached from
        register, and from a login that was refused as unverified.
      */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/*
        Public, like /login and the landing page: the pricing page has to
        render for a visitor who has never signed up, and its data source
        (GET /api/pricing) needs no auth either. Signing in only changes
        the CTA - see PricingPage.jsx.
      */}
      <Route path="/pricing" element={<PricingPage />} />
      {/*
        Public. A visitor is sent on to the landing page's #contact
        section, where the form lives; a signed-in user (who never sees
        the landing page) gets the same form here, prefilled - that is
        where the in-app plan page's Custom card leads.
      */}
      <Route path="/contact" element={<ContactPage />} />
      {/* Public and unauthenticated on purpose: someone evaluating
          KrishvaQ should be able to download the desktop app before
          they have an account, and the app can create one itself. */}
      <Route path="/download" element={<DownloadPage />} />
      {/* Public and reachable without an account: someone deciding
          whether to sign up has to be able to read what they would
          be agreeing to first. */}
      <Route path="/privacy-policy" element={<LegalPage document="privacy" />} />
      <Route path="/terms-of-service" element={<LegalPage document="terms" />} />
      {/* The standalone Refund Policy was folded into the Terms of Service. */}
      <Route path="/refund-policy" element={<Navigate to="/terms-of-service" replace />} />
      {/* The original URLs keep working (registration page, consent
          prompt, the desktop app's "open on the web" links, bookmarks). */}
      <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
      <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route path="/app" element={<ProtectedRoute />}>
        {/*
          Live Meeting intentionally sits outside AppShell: it needs the
          full viewport (no sidebar/topbar chrome) to maximize meeting
          workspace, per the live-meeting UX requirement.
        */}
        <Route
          path="meeting/:meetingId/live"
          element={<LiveMeetingPage />}
        />

        <Route element={<AppShell />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="memory" element={<AskCoPilotPage />} />
          <Route path="meeting/new" element={<StartMeetingPage />} />
          <Route path="history" element={<MeetingHistoryPage />} />
          <Route path="auto-attend" element={<AutoAttendPage />} />
          {/* Legacy URL, kept so existing bookmarks keep working. */}
          <Route
            path="notetaker"
            element={<Navigate to="/app/auto-attend" replace />}
          />
          <Route path="meeting/:meetingId" element={<MeetingDetailsPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          {/*
            Documents moved out from under Settings into its own primary
            Workspace nav item (components/layout/AppShell.jsx) - it holds
            every document a user has created (uploaded or typed) across
            ALL meetings, reusable in any future meeting, so it reads as
            workspace-level content rather than an account setting.
            Redirect keeps any old /app/settings/documents bookmark alive.
          */}
          <Route
            path="settings/documents"
            element={<Navigate to="/app/documents" replace />}
          />
          {/*
            Plan & Billing - its own top-level Workspace destination
            (components/layout/AppShell.jsx), not a Settings sub-route:
            it is the user's whole subscription surface (current plan,
            usage, payment status, billing history, cancellation),
            which reads as workspace-level content rather than an
            account setting. Redirect keeps any old
            /app/settings/billing bookmark alive.
          */}
          <Route path="billing" element={<BillingPage />} />
          <Route
            path="settings/billing"
            element={<Navigate to="/app/billing" replace />}
          />
          {/*
            In-app plan picker. Kept under AppShell so a signed-in user
            changing plans keeps the sidebar/topbar and a "back to
            billing" path - the public /pricing route redirects here for
            anyone signed in (pages/PricingPage.jsx).
          */}
          <Route path="billing/plans" element={<PlansPage />} />
          <Route path="profile" element={<Navigate to="/app/settings" replace />} />
          {/*
            Compatibility redirect for old /app/legacy-meeting bookmarks
            - the legacy mic/tab-audio -> question-detection -> OpenAI
            page (and its backend /stream-stt, /get-answers endpoints)
            has been removed entirely. Sends an old link to the current
            meeting flow instead of a dead route or a confusing login
            bounce.
          */}
          <Route
            path="legacy-meeting"
            element={<Navigate to="/app/meeting/new" replace />}
          />
        </Route>
      </Route>

      <Route path="/admin" element={<AdminRoute />}>
        <Route element={<AdminShell />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboardPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="users/:userId" element={<AdminUserDetailsPage />} />
          <Route path="pricing" element={<AdminPricingPage />} />
          <Route path="billing" element={<AdminBillingPage />} />
          <Route path="meetings" element={<AdminMeetingsPage />} />
          <Route
            path="meetings/:meetingId"
            element={<AdminMeetingDetailsPage />}
          />
          <Route path="system" element={<AdminSystemPage />} />
          <Route path="release-emails" element={<AdminReleaseNotificationsPage />} />
          <Route path="audit" element={<AdminAuditPage />} />
        </Route>
      </Route>

      <Route path="/" element={<LandingPage />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
